import * as React from 'react';
import {
  BaSeBreakpointContext,
  BaSeButton,
  BaSeDialogContext,
  BaSeHeading5,
  BaSeOptionPicker,
  BaSeShapeButton,
  BaSeSmall1,
  BaSeTag,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import {
  CardHeader,
  CardHeaderButtons,
} from '../../components/new-relatorios/fluxo/relatorio-fluxo-lista-styled';
import { AppContext } from '../../components/shared/context';
import { RelatorioContato } from '../../components/new-relatorios/contato/relatorio-contato';
import { RelatorioFluxoConsolidado } from '../../components/new-relatorios/fluxo/relatorio-fluxo-consolidado';
import { ShowButtonsAction } from '../../components/new-relatorios/head-buttons-action';
import { ButtonOptionPicker } from './new-relatorios-styled';
import { RelatorioContegoria } from '../../components/new-relatorios/categoria/relatorio-categoria';
import { GraficoRelatorioCategoria } from '../../components/new-relatorios/categoria/grafico-relatorio-categoria';
import { useRelatorio } from '../../services/relatorio/RelatorioService';
import { ModalShare } from '../../components/new-relatorios/modal-share';
import { enumRelatorios } from '../../constants/relatorions';

const DEFAULT_COLOR_TEXT = BaSeTheme.colors.institucionais.cinzaSebrae20;

export const colunas = [
  { id: 1, label: 'Categoria', tipo: 'CATEGORIA' },
  { id: 2, label: 'Tipo de receita', tipo: 'TIPO_RECEITA' },
  { id: 3, label: 'Pagamento/Recebimento', tipo: 'PAGAMENTO' },
  { id: 4, label: 'Vencimento/Receita', tipo: 'VENCIMENTO' },
  { id: 5, label: 'Situação', tipo: 'SITUACAO' },
];

const charPieAlt = 'chart-pie-alt';
const listUl = 'list-ul';
const graphBar = 'graph-bar';

export const ShowMoney = ({ value, forceNegative }) => {
  const money = Sebrae.useMask(parseFloat(value ?? '0').toFixed(2), 'decimal');
  const negative = React.useMemo(
    () => (forceNegative || money.includes('-') ? '-' : ''),
    [money, forceNegative],
  );

  return (
    <div style={{ minWidth: 114 }}>
      <BaSeSmall1 isBold color={DEFAULT_COLOR_TEXT}>
        {negative} R$ {money.replace('-', '')}
      </BaSeSmall1>
    </div>
  );
};

export const SituacaoTags = ({ value }) => {
  const color = React.useMemo(() => {
    const colors = {
      'A vencer': 'default',
      'Vence hoje': 'attention',
      Vencido: 'destructive',
      Pago: 'confirmation',
      Atrasado: 'destructive',
      Recebido: 'confirmation',
    };
    return colors?.[value] ?? 'default';
  }, [value]);

  return <BaSeTag color={color} label={value} />;
};

export const RelatoriosLancamentos = ({
  lancamentosRelatorio,
  nomeRelatorio,
  startDateBroken,
  endDateBroken,
  typeReport,
  paramsFilter,
  isPublic = false,
  colunasPersonalizadas,
  // eslint-disable-next-line
}) => {
  const { empresa } = React.useContext(AppContext);
  const { inTablet } = React.useContext(BaSeBreakpointContext);
  const { showNotification, showModal } = React.useContext(BaSeDialogContext);
  const {
    relatorioContatoDownload,
    relatorioContaDownload,
    relatorioCategoriaDownload,
    relatorioCategoriaGraficosDownload,
    compartilharRelatorio,
  } = useRelatorio();
  const [loadingDownload, setLoadingDownload] = React.useState(false);
  const [blobPdf, setBlobPdf] = React.useState(null);
  const [isColumnCustom, setIsColumnCustom] = React.useState(false);
  const [isShowChart, setIsShowChart] = React.useState(false);
  const [isShowChartBar, setIsShowChartBar] = React.useState(false);
  const [isExpandRows, setIsExpandRows] = React.useState(false);
  const [isBreak, setIsBreak] = React.useState(false);
  const [svgReceita, setSvgReceita] = React.useState(null);
  const [svgDespesa, setSvgDespesa] = React.useState(null);
  const [isDisabledExport, setIsDisabledExport] = React.useState(false);
  const [columnsSelected, setColumnsSelected] = React.useState(
    isPublic ? colunasPersonalizadas : [colunas[0], colunas[3], colunas[4]],
  );
  const [showColumnsSelected, setShowColumnsSelected] =
    React.useState(columnsSelected);
  const { cinzaSebrae20 } = BaSeTheme.colors.institucionais;
  const downloadRef = React.useRef(null);

  const isCategoria = React.useMemo(
    () => nomeRelatorio === typeReport.CATEGORIA,
    [nomeRelatorio, typeReport],
  );

  const isContato = React.useMemo(
    () => nomeRelatorio === typeReport.CONTATO,
    [nomeRelatorio, typeReport],
  );

  const messageError = React.useCallback(
    (error) =>
      showNotification({
        message: `Não foi possível exportar o relatório`,
        supportMessage: error?.message ?? '',
        color: 'destructive',
      }),
    [showNotification],
  );

  const download = React.useCallback((data) => {
    setBlobPdf(window.URL.createObjectURL(data));
    setTimeout(() => {
      downloadRef.current.click();
    });
  }, []);

  const downloadRelatorio = React.useCallback(() => {
    const getDownload = isCategoria
      ? relatorioCategoriaDownload
      : isContato
      ? relatorioContatoDownload
      : relatorioContaDownload;
    const columnsParams = columnsSelected?.map?.(
      (item) => `campoCustomizado=${item?.tipo}`,
    );

    const params = columnsSelected.length
      ? [...paramsFilter, ...columnsParams]
      : [...paramsFilter];

    setLoadingDownload(true);
    getDownload(empresa?.id, params.join('&'))
      .then((data) => {
        download(data);
      })
      .catch((error) => messageError(error))
      .finally(() => {
        setLoadingDownload(false);
      });
  }, [
    isCategoria,
    relatorioCategoriaDownload,
    isContato,
    relatorioContatoDownload,
    relatorioContaDownload,
    columnsSelected,
    paramsFilter,
    empresa,
    download,
    messageError,
  ]);

  const downloadRelatorioCategoria = React.useCallback(() => {
    const body = new FormData();
    let fonc = relatorioCategoriaDownload;
    if (inTablet && (svgReceita || svgDespesa)) {
      svgReceita && body.append('graficoReceitaSVG', svgReceita);
      svgDespesa && body.append('graficoDespesaSVG', svgDespesa);
      fonc = relatorioCategoriaGraficosDownload;
    }
    setLoadingDownload(true);
    fonc(empresa?.id, paramsFilter.join('&'), body)
      .then((data) => {
        download(data);
      })
      .catch((error) => messageError(error))
      .finally(() => {
        setLoadingDownload(false);
      });
  }, [
    download,
    empresa,
    inTablet,
    messageError,
    paramsFilter,
    relatorioCategoriaDownload,
    relatorioCategoriaGraficosDownload,
    svgDespesa,
    svgReceita,
  ]);

  const handleColumnCusttom = React.useCallback(
    () => setIsColumnCustom((current) => !current),
    [],
  );

  const handleShowModal = React.useCallback(() => {
    const columnsSelected = showColumnsSelected?.map((item) => item?.tipo);
    showModal({
      title: 'Compartilhar',
      actions: [],
      size: 'large',
      content: ({ closeModal }) => (
        <ModalShare
          closeModal={closeModal}
          filters={paramsFilter}
          tipoRelatorio={enumRelatorios[nomeRelatorio]}
          columnsSelected={columnsSelected}
          callback={compartilharRelatorio}
        />
      ),
    });
  }, [
    nomeRelatorio,
    paramsFilter,
    compartilharRelatorio,
    showColumnsSelected,
    showModal,
  ]);

  return (
    <>
      <CardHeader>
        <BaSeHeading5 isBold color={cinzaSebrae20}>
          {nomeRelatorio}
        </BaSeHeading5>
        {!isPublic && (
          <CardHeaderButtons>
            {!isCategoria && (
              <div>
                {inTablet ? (
                  <BaSeButton
                    value="Personalizar colunas"
                    rightIcon="web-section-alt"
                    onClick={handleColumnCusttom}
                    type="secondary"
                  />
                ) : (
                  <BaSeShapeButton
                    shape="square"
                    nameIcon="web-section-alt"
                    onClick={handleColumnCusttom}
                    type="secondary"
                  />
                )}
                {isColumnCustom && (
                  <ButtonOptionPicker isMobile={!inTablet}>
                    <BaSeOptionPicker
                      color={BaSeTheme.colors.institucionais.azulSebrae}
                      withFilterText={false}
                      values={colunas}
                      emptyValueLabel="Selecione"
                      onChange={setColumnsSelected}
                      searchPlaceholder="Pesquisar"
                      maxMenuHeight="360px"
                      size="medium"
                      menuAlwaysIsOpen={true}
                      initialSelectedValuesId={columnsSelected.map(
                        (item) => item.id,
                      )}
                    />
                    <div>
                      <BaSeButton
                        value="Aplicar"
                        onClick={() => {
                          setShowColumnsSelected(columnsSelected);
                          setIsColumnCustom(false);
                        }}
                        color={BaSeTheme.colors.institucionais.azulSebrae}
                      />
                    </div>
                  </ButtonOptionPicker>
                )}
              </div>
            )}
            {isCategoria && (
              <div>
                {inTablet ? (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <BaSeButton
                      value={isShowChart ? 'Ver tabela' : 'Ver gráficos'}
                      leftIcon={isShowChart ? listUl : charPieAlt}
                      onClick={() => setIsShowChart((e) => !e)}
                      type="tertiary"
                      color="default"
                    />
                    {isShowChart && (
                      <BaSeButton
                        value={
                          isShowChartBar
                            ? 'Ver gráfico de pizza'
                            : 'Ver gráfico de barras'
                        }
                        leftIcon={isShowChartBar ? charPieAlt : graphBar}
                        onClick={() => setIsShowChartBar((e) => !e)}
                        type="tertiary"
                        color="default"
                      />
                    )}
                    {!isShowChart && (
                      <BaSeButton
                        value={
                          isExpandRows ? 'Recolher linhas' : 'Expandir linhas'
                        }
                        leftIcon={
                          isExpandRows ? 'arrow-head-up' : 'arrow-head-down'
                        }
                        onClick={() => {
                          setIsExpandRows((e) => !e);
                          setIsBreak(true);
                        }}
                        type="secondary"
                      />
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <BaSeShapeButton
                      nameIcon={isShowChart ? listUl : charPieAlt}
                      onClick={() => setIsShowChart((e) => !e)}
                      type="tertiary"
                      color="default"
                      shape="square"
                    />
                    {isShowChart && (
                      <BaSeShapeButton
                        nameIcon={isShowChartBar ? charPieAlt : graphBar}
                        onClick={() => setIsShowChartBar((e) => !e)}
                        type="tertiary"
                        color="default"
                        shape="square"
                      />
                    )}
                    {!isShowChart && (
                      <BaSeShapeButton
                        nameIcon={
                          isExpandRows ? 'arrow-head-up' : 'arrow-head-down'
                        }
                        onClick={() => {
                          setIsExpandRows((e) => !e);
                          setIsBreak(true);
                        }}
                        type="secondary"
                        shape="square"
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            <ShowButtonsAction
              download={
                isCategoria ? downloadRelatorioCategoria : downloadRelatorio
              }
              loadingDownload={loadingDownload}
              print={() => window.print()}
              isDisabled={isDisabledExport}
              showModal={handleShowModal}
            />
          </CardHeaderButtons>
        )}
        {isPublic &&
          (inTablet ? (
            <BaSeButton
              value="Imprimir"
              leftIcon="print"
              onClick={() => window.print()}
              type="secondary"
            />
          ) : (
            <BaSeShapeButton
              shape="square"
              nameIcon="print"
              onClick={() => window.print()}
              type="secondary"
            />
          ))}
      </CardHeader>
      {!isCategoria &&
        lancamentosRelatorio?.listaPeriodo?.map((item, i) => (
          <div style={{ marginBottom: 20 }} key={i}>
            <RelatorioContato
              download={downloadRelatorio}
              loadingDownload={loadingDownload}
              dadosLancamento={item}
              columnsSelected={showColumnsSelected}
            />
          </div>
        ))}
      {isCategoria &&
        !isShowChart &&
        lancamentosRelatorio?.listaPorTipoLancamento?.map(
          (item, i) =>
            !!item?.listaCategorias?.length && (
              <div style={{ marginBottom: 20 }} key={i}>
                <RelatorioContegoria
                  dadosLancamento={item}
                  columnsSelected={showColumnsSelected}
                  isExpandRows={isExpandRows}
                  isBreak={isBreak}
                  setIsBreak={setIsBreak}
                  tipoLancamento={item?.tipo}
                />
              </div>
            ),
        )}
      {isCategoria &&
        isShowChart &&
        lancamentosRelatorio?.listaPorTipoLancamento?.map(
          (item, i) =>
            !!item?.listaCategorias?.length && (
              <GraficoRelatorioCategoria
                key={i}
                dadosLancamento={item}
                isShowChartBar={isShowChartBar}
                setSvgReceita={setSvgReceita}
                setSvgDespesa={setSvgDespesa}
                setIsDisabledExport={setIsDisabledExport}
              />
            ),
        )}
      <RelatorioFluxoConsolidado
        dados={lancamentosRelatorio?.consolidado}
        broken={startDateBroken || endDateBroken}
      />
      <div style={{ display: 'none', position: 'absolute' }}>
        <a
          download={`${empresa?.cnpj} - ${nomeRelatorio}.pdf`}
          href={blobPdf}
          alt="baixar pdf dos relatórios"
          ref={downloadRef}
        >
          .
        </a>
      </div>
    </>
  );
};
